import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TextInput } from '@/components/inputs/text-input';
import { TextArea } from '@/components/inputs/text-area';
import { Button } from '@/components/button';
import { Form } from '@/components/form';
import { requiredEnum, requiredPhoneNumber, requiredString, zt } from '@/util/validators';
import styles from './support-page.module.css';
import { useTranslation } from '@/contexts/i18n-context';
import { useGetIssueTypes } from '@/api/get-issue-types';
import { useCreateSupport } from '@/api/create-support-case';
import { useNavigate } from '@/util/react-router-dom';
import { Select } from '@/components/inputs/select';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { env } from '@/env';
import { GetPersonalInfoResponse, SupportIssueType } from '@/api/api.schemas';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { useMediaQuery } from '@/hooks/use-media-query';
import clsx from 'clsx';
import { useBranding } from '@/outlets/branding-outlet';
import { PhoneInput } from '@/components/inputs/phone-input';
import { Banner } from '@/components/banner';
import { Separator } from '@/components/separator';
import { useCurrentUser } from '@/contexts/current-user-context';
import { useGetPatient } from '@/api/get-personal-info';
import { Loader } from '@/components/loaders/loader';
import { formatPhoneNumber, isUserLoggedIn } from '@/util/helpers';
import { useState } from 'react';
import { SupportSuccessModal } from './support-success-modal';

const supportSchema = z.object({
  firstName: requiredString(),
  lastName: requiredString(),
  email: requiredString().email(zt('common.errors.email')),
  phone: requiredPhoneNumber(zt('support.form.invalidPhone')),
  issueType: requiredEnum(SupportIssueType),
  description: requiredString(),
});

type FormValues = z.infer<typeof supportSchema>;

export function SupportPage() {
  const { user } = useCurrentUser();
  const isLoggedIn = isUserLoggedIn(user);
  const { data: patient, isFetching } = useGetPatient(user?.id ?? '', {
    query: { enabled: isLoggedIn },
  });

  if (isLoggedIn && isFetching) return <Loader type="large" />;

  return <SupportForm patient={patient} />;
}

function SupportForm({ patient }: { patient?: GetPersonalInfoResponse }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const contact = patient?.surrogate ?? patient;

  const defaultValues = contact
    ? {
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: formatPhoneNumber(
          patient?.surrogate ? patient.surrogate.phone : (patient?.cellPhone ?? patient?.homePhone),
        ),
      }
    : undefined;

  const form = useForm<FormValues>({
    resolver: zodResolver(supportSchema),
    mode: 'onBlur',
    defaultValues,
  });
  const { networkId } = useBranding();

  const isMobile = useMediaQuery('--screen-md');

  const onBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const { data: issueTypes } = useGetIssueTypes({ networkId });
  const { mutate: createSupport, isPending, error } = useCreateSupport();

  const onSubmit = (data: FormValues) => {
    createSupport(
      { data: { ...data, networkId } },
      {
        onSuccess: () => setIsSuccessModalOpen(true),
      },
    );
  };

  const onCloseModal = () => {
    setIsSuccessModalOpen(false);
    form.resetField('description');
    form.resetField('issueType');
  };

  return (
    <PageContainer>
      {!isMobile && (
        <Button variant="link" leftIcon="arrow_back" onClick={onBack} className={styles.navButton}>
          {t('common.back')}
        </Button>
      )}
      <GradientFormTitle withParagraphMargins={false}>
        {isMobile && (
          <Button
            variant="link"
            leftIcon="arrow_back"
            onClick={onBack}
            className={styles.navButton}
          >
            {t('common.back')}
          </Button>
        )}
        <div className={styles.titleContainer}>
          <h1 className={clsx('h3', styles.title)}>{t('support.title')}</h1>
          <p className="semibold">{t('support.description')}</p>
        </div>
      </GradientFormTitle>

      <Form form={form} onSubmit={onSubmit}>
        <Section className={styles.formContainer}>
          <Banner type="warning">{t('support.bannerText')}</Banner>
          {error && (
            <Banner type="error" focusOnMount>
              {t('common.errors.unknown')}
            </Banner>
          )}

          <div className={styles.supportContainer}>
            <TextInput
              name="firstName"
              label={t('support.form.labels.firstName')}
              placeholder={t('support.form.placeholders.firstName')}
              withIconsIndicators={false}
            />

            <TextInput
              name="lastName"
              label={t('support.form.labels.lastName')}
              placeholder={t('support.form.placeholders.lastName')}
              withIconsIndicators={false}
            />

            <TextInput
              name="email"
              type="email"
              label={t('support.form.labels.email')}
              placeholder={t('support.form.placeholders.email')}
              withIconsIndicators={false}
            />

            <PhoneInput
              name="phone"
              label={t('support.form.labels.phone')}
              withIconsIndicators={false}
            />

            <Select
              name="issueType"
              label={t('support.form.labels.issueType')}
              placeholder={t('support.form.placeholders.selectIssue')}
              options={
                issueTypes?.map((issue) => ({
                  value: issue.id.toString(),
                  label: issue.description,
                })) ?? []
              }
            />

            <TextArea
              className={styles.description}
              name="description"
              label={t('support.form.labels.description')}
              placeholder={t('support.form.placeholders.description')}
              rows={3}
              maxLength={1000}
            />
          </div>

          <Separator />

          <div className={styles.needHelpContainer}>
            <p className="semibold">{t('support.needHelp.title')}</p>
            <p>
              {t('support.needHelp.description', {
                email: (
                  <span key="support-email" className="bold">
                    {env.VITE_SUPPORT_EMAIL}
                  </span>
                ),
                phone: (
                  <span key="support-phone" className="bold">
                    {env.VITE_SUPPORT_PHONE}
                  </span>
                ),
              })}
            </p>
          </div>
        </Section>

        <FormButtonRow
          buttonTextKey="common.submit"
          type="submit"
          variant="filled"
          loading={isPending}
          secondaryButton={
            <Button onClick={onBack} variant="outlined" color="accent" disabled={isPending}>
              {t('common.cancel')}
            </Button>
          }
        />
      </Form>

      <SupportSuccessModal
        isOpen={isSuccessModalOpen}
        onSubmit={() => navigate('/')}
        onClose={onCloseModal}
      />
    </PageContainer>
  );
}
